import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { debounce } from 'lodash';
import {
  clearEmployeesList,
  getEmployeesList,
  approveEmployee,
  rejectEmployee,
} from 'redux/actions/EmployeesAction';
import { fetchCompanyList } from 'redux/actions/companyAction';
import { onLoaderTrigger } from 'redux/actions/commonAction';
import CommonSubHeader from 'components/commonSubHeader/CommonSubHeader';
import CommonTable from 'components/commonTable/CommonTable';
import Loader from 'components/loader/Loader';
import CommonModal from 'components/commonModal/commonModal';
import BulkEmployeeUpload from 'components/bulkEmployeeUpload/BulkEmployeeUpload';
import BulkEmployeeUploadResultModal from 'components/bulkEmployeeUpload/BulkEmployeeUploadResultModal';
import { handlePermissions } from 'utils/utils';
import { roleAndPermissionEnum } from 'utils/rolesPermissionsEnum';

const Employee = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- URL Params ---
  const pageParams = searchParams?.get('page');
  const pageSizeParams = searchParams?.get('pageSize');
  const searchVal = searchParams?.get('search');
  const companyIdParam = searchParams?.get('companyId');
  const companyNameParam = searchParams?.get('companyName');

  let paramsList = {};
  searchParams.forEach((value, key) => {
    paramsList[key] = value;
  });

  const employeeRoleEnum = roleAndPermissionEnum.employee;

  const { state } = useLocation();

  // --- Local State ---
  const [page, setPage] = useState(pageParams ? parseInt(pageParams) : state?.payload?.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    pageSizeParams ? parseInt(pageSizeParams) : state?.payload?.pageSize || 10,
  );
  const [search, setSearch] = useState(searchVal || state?.payload?.search || '');
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [resultModal, setResultModal] = useState(false);
  const [failedRows, setFailedRows] = useState([]);

  // --- Selected Company (dropdown) - URL params se initialize karo taaki persist rahe ---
  const [selectedCompany, setSelectedCompany] = useState(
    companyIdParam && companyNameParam
      ? { value: companyIdParam, label: companyNameParam }
      : state?.payload?.selectedCompany || null,
  );

  // --- Company ---
  const companyID =
    localStorage.getItem('subCompany')?.length > 0 &&
    JSON.parse(localStorage.getItem('subCompany'));

  // --- Payload ---

  let employeeListPayload = {
    search: search,
    page: page + 1,
    pageSize: rowsPerPage,
    companyId: companyID?.value || selectedCompany?.value || '',
    status: '',
  };

  // --- Redux Selectors ---
  const employeeTableData = useSelector((state) => state?.getEmployeesList?.employeesList?.data);
  const tableLoader = useSelector((state) => state?.getEmployeesList?.loading);
  const roleAndPermissions = useSelector(
    (state) => state?.getRolesAndPermissions?.rolesAndPermissions,
  );

  // --- Company List for Dropdown ---
  const companyTableData = useSelector((state) => state?.userCompany?.companyList);
  const companyOption =
    companyTableData?.body?.map((val) => {
      return { value: val?.id, label: val?.name };
    }) || [];

  // --- Inject approvalStatus header if missing ---
  // const employeeTableDataWithStatus = {
  //   ...employeeTableData,
  //   header: employeeTableData?.header?.some(
  //     (item) => item.key === "approvalStatus",
  //   )
  //     ? employeeTableData.header
  //     : [
  //         ...(employeeTableData?.header || []),
  //         { label: "Status", key: "approvalStatus" },
  //       ],
  // };

  const employeeTableDataWithStatus = {
    ...employeeTableData,
    header: [
      ...(employeeTableData?.header || []),
      ...(!employeeTableData?.header?.some((item) => item.key === 'approvalStatus')
        ? [{ label: 'Status', key: 'approvalStatus' }]
        : []),
      ...(!employeeTableData?.header?.some((item) => item.key === 'action')
        ? [{ label: 'Action', key: 'action' }]
        : []),
    ],
  };

  useEffect(() => {
    if (!companyID) {
      dispatch(
        fetchCompanyList({
          search: '',
          page: 0,
          pageSize: 1000,
          status: [],
          location: [],
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (companyID?.value || selectedCompany?.value) {
      dispatch(getEmployeesList(employeeListPayload));
    }
  }, [state, selectedCompany]);

  useEffect(() => {
    dispatch(onLoaderTrigger(false));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearEmployeesList());
    };
  }, []);

  // --- Navigation ---
  const pageNavigation = () => {
    navigate('/employees/new-employee', {
      state: { selectedCompany },
    });
  };

  // --- Table Row Click ---
  const tableOnClickRow = (navigate, val) => {
    navigate(`/employees/employee-profile/${val.id}`, {
      state: {
        id: val.id,
        type: 'edit',
        payload: employeeListPayload,
        selectedCompany,
      },
    });
  };

  // --- Pagination ---
  const handleChangePage = (event, newPage) => {
    window.history.replaceState({}, '');
    setSearchParams({ ...paramsList, page: newPage });
    setPage(newPage);
    dispatch(getEmployeesList({ ...employeeListPayload, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    window.history.replaceState({}, '');
    setSearchParams({
      ...paramsList,
      page: 0,
      pageSize: parseInt(event.target.value),
    });
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    dispatch(
      getEmployeesList({
        ...employeeListPayload,
        page: 1,
        pageSize: parseInt(event.target.value),
      }),
    );
  };

  // --- Search ---
  const onSearchQuery = debounce((val) => {
    setSearchParams({ ...paramsList, search: val, page: 0, pageSize: '' });
    window.history.replaceState({}, '');
    setSearch(val);
    setPage(0);
    dispatch(
      getEmployeesList({
        ...employeeListPayload,
        search: val,
        pageSize: '',
        page: 1, // Backend expects 1-indexed
      }),
    );
  }, 500);

  // --- Company Dropdown Change ---
  const onCompanyChange = (event, value) => {
    setSelectedCompany(value);
    setPage(0);
    setSearch('');
    setSearchParams({
      ...paramsList,
      page: 0,
      search: '',
      companyId: value?.value || '',
      companyName: value?.label || '',
    });
  };

  // --- Approve Employee ---
  // const onApproveEmployee = (row) => {
  //   dispatch(
  //     approveEmployee({
  //       companyId: row.companyId,
  //       employeeId: row.id,
  //       search,
  //       page,
  //       pageSize: rowsPerPage,
  //     }),
  //   );
  // };

  // --- Approve/Reject Employee ---
  const onApproveEmployee = (row, status) => {
    const payload = {
      companyId: row.companyId,
      employeeId: row.id,
      search,
      page: page + 1,
      pageSize: rowsPerPage,
    };

    //  Safely check if status implies rejection (handles "Rejected", "Reject", false, etc.)
    const isRejected = status?.toString().toLowerCase().includes('reject');

    if (isRejected) {
      dispatch(rejectEmployee(payload));
    } else {
      dispatch(approveEmployee(payload));
    }
  };

  // --- Bulk Upload ---
  const handleBulkUploadResult = (failed) => {
    setBulkUploadModal(false);
    setFailedRows(failed);
    setResultModal(true);
  };

  // --- Permissions ---
  const accessForAdd = handlePermissions(roleAndPermissions, employeeRoleEnum.moduleKey, {
    defaultAllAccess: employeeRoleEnum.all,
    customAccess: employeeRoleEnum.add,
  });

  // --- Company select hua hai ya nahi (dropdown wale flow ke liye) ---
  const showCompanySelectMessage = !companyID && !selectedCompany;

  return (
    <Box className="mb20">
      <Box className="mb30">
        <CommonSubHeader
          navigation={pageNavigation}
          buttonName={accessForAdd ? 'Add Employee' : ''}
          onSearchQuery={onSearchQuery}
          filter={false}
          tabName={'company'}
          search={search}
          showUploadExcel={true}
          openBulkUpload={() => setBulkUploadModal(true)}
          disableAdd={!companyID && !selectedCompany}
          showCompanyDropdown={!companyID}
          companyOptions={companyOption}
          selectedCompany={selectedCompany}
          onCompanyChange={onCompanyChange}
        />
      </Box>

      {showCompanySelectMessage ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40vh',
          }}
        >
          <Typography variant="body1" className="colorGrey">
            Please select a company to view employees
          </Typography>
        </Box>
      ) : tableLoader ? (
        <Loader height={'60vh'} />
      ) : (
        <CommonTable
          data={employeeTableDataWithStatus}
          tableOnClickRow={tableOnClickRow}
          page={page}
          isClickable={true}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          onApproveEmployee={onApproveEmployee}
          tabName={'employee'}
          showApprovalAction={true}
        />
      )}

      {/* Bulk Upload Modal */}
      <CommonModal open={bulkUploadModal} handleClose={() => setBulkUploadModal(false)}>
        <BulkEmployeeUpload
          handleClose={() => setBulkUploadModal(false)}
          onUploadResult={handleBulkUploadResult}
          selectedCompany={selectedCompany}
        />
      </CommonModal>

      {/* Bulk Upload Result Modal */}
      <CommonModal
        open={resultModal}
        handleClose={() => {
          setResultModal(false);
          setFailedRows([]);
        }}
      >
        <BulkEmployeeUploadResultModal
          failedRows={failedRows}
          handleClose={() => {
            setResultModal(false);
            setFailedRows([]);
          }}
        />
      </CommonModal>
    </Box>
  );
};

export default Employee;
